import { type ReactNode } from 'react';
import { type RequestContext } from '@cx-web/domain-types';
import format from 'string-format';

import {
  type BettingInsightsContent,
  type BettingInsightsResult,
  type BettingInsightsResultAttribute,
  type EsIndex,
} from '../types';
import { formatDate } from '../utils/formatDate';

// Type for the context that will be passed as first parameter to functions
export interface BettingInsightsTitleContext {
  environment: RequestContext['environment'];
  featureFlags: RequestContext['featureFlags'];
  repubConfig: RequestContext['repubConfig'];
  user: RequestContext['user'];
}

export interface BettingInsightsTitleConfig<
  TIndex extends EsIndex,
  TResult extends BettingInsightsResult<TIndex> = BettingInsightsResult<TIndex>,
  TContent extends BettingInsightsContent<TIndex>[] = BettingInsightsContent<TIndex>[],
  TAttribute extends BettingInsightsResultAttribute<TIndex> = BettingInsightsResultAttribute<TIndex>,
> {
  format: string | (({ content, result }: { content: TContent; result: TResult }) => string);
  getResultOverrides?: (
    context: BettingInsightsTitleContext,
    {
      content,
      result,
    }: {
      content: TContent;
      result: TResult;
    },
  ) => Partial<Record<TAttribute, unknown>> | undefined;
  render?: (
    context: BettingInsightsTitleContext,
    { content, result }: { content: TContent; result: TResult },
  ) => ReactNode;
}

// Type alias for the return type of createBettingInsightsTitle
export type BettingInsightsTitle<
  TIndex extends EsIndex,
  TResult extends BettingInsightsResult<TIndex> = BettingInsightsResult<TIndex>,
  TContent extends BettingInsightsContent<TIndex>[] = BettingInsightsContent<TIndex>[],
  TAttribute extends BettingInsightsResultAttribute<TIndex> = BettingInsightsResultAttribute<TIndex>,
> = Readonly<{
  format: string | (({ content, result }: { content: TContent; result: TResult }) => string);
  getResultOverrides?: ({
    content,
    result,
  }: {
    content: TContent;
    result: TResult;
  }) => Partial<Record<TAttribute, unknown>> | undefined;
  render: ({ content, result }: { content: TContent; result: TResult }) => ReactNode;
}>;

/**
 * Factory function to create BettingInsightsTitle configuration (functional approach)
 * @param requestContext - The request context
 * @param config - The title configuration
 * @returns Immutable title configuration object
 */
export function createBettingInsightsTitle<
  TIndex extends EsIndex,
  TResult extends BettingInsightsResult<TIndex> = BettingInsightsResult<TIndex>,
  TContent extends BettingInsightsContent<TIndex>[] = BettingInsightsContent<TIndex>[],
  TAttribute extends BettingInsightsResultAttribute<TIndex> = BettingInsightsResultAttribute<TIndex>,
>(
  requestContext: RequestContext,
  config: BettingInsightsTitleConfig<TIndex, TResult, TContent, TAttribute>,
): BettingInsightsTitle<TIndex, TResult, TContent, TAttribute> {
  // Create context object
  const context: BettingInsightsTitleContext = {
    environment: requestContext.environment,
    featureFlags: requestContext.featureFlags,
    repubConfig: requestContext.repubConfig,
    user: requestContext.user,
  };

  // Default render function that uses context
  const defaultRender = ({ content, result }: { content: TContent; result: TResult }): ReactNode => {
    const template =
      typeof config.format === 'function' ? config.format({ content, result }) : (config.format as string);
    return format(template, {
      ...result,
      ...content[0],
      subeventStartTime: formatDate(result.subeventStartTime, undefined, context.repubConfig),
      ...(config.getResultOverrides ? config.getResultOverrides(context, { content, result }) : {}),
    });
  };

  // Create the result object with wrapper functions that inject context
  const resultObj = {
    // Config properties
    format: config.format,

    // Wrapper functions that inject context as first parameter
    getResultOverrides: config.getResultOverrides
      ? ({ content, result }: { content: TContent; result: TResult }) =>
          config.getResultOverrides!(context, { content, result })
      : undefined,
    render: ({ content, result }: { content: TContent; result: TResult }) =>
      config.render ? config.render(context, { content, result }) : defaultRender({ content, result }),
  };

  return Object.freeze(resultObj);
}
